/**
 * TrialBanner — screen 9a · trial banner (Phase 2 styled), inside the app.
 *
 * Design: layout/9a-trial-banner-{9-days-left,2-days-left-urgent,mobile}.txt.
 * Renders above the page content for a workspace on a PAID trial: a calm teal
 * banner at 3+ days left, an urgent amber one at 2 days or fewer.
 *
 * DATA SOURCE — real, browser-readable: the signed-in person's own `workspaces`
 * row (`workspaces_select` scopes to `current_user_workspace()`), which carries
 * `plan`, `trial_started_at` and `trial_ends_at` (all server-set by 0056). The
 * \"days left\" is computed HERE from `trial_ends_at`, never guessed.
 *
 * Shown only while plan = 'pro' AND the trial end is in the future. When the
 * trial has ENDED the workspace falls back to the Free plan server-side, so
 * this banner disappears and the trial-ended page (screen 9b) takes over.
 * Failing to load the workspace row hides the banner (absent, not a fake 0).
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useWorkspaceId } from '../../context/AuthContext';
import { isSignupEnabled } from '../../lib/signupEnabled';
import './TrialBanner.css';

const DAY_MS = 24 * 60 * 60 * 1000;

function daysLeft(trialEndsAt: string): number {
  const end = new Date(trialEndsAt).getTime();
  return Math.max(1, Math.ceil((end - Date.now()) / DAY_MS));
}

export default function TrialBanner() {
  const { workspaceId, settled } = useWorkspaceId();
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'off' }
    | { status: 'on'; daysLeft: number; endsAt: Date }
  >({ status: 'loading' });

  useEffect(() => {
    // ⛔ LAUNCH SWITCH: with REACT_APP_SIGNUP_ENABLED OFF, the banner must not
    // even RUN its query — production has no trial columns yet.
    if (!isSignupEnabled() || !settled || !workspaceId) {
      setState({ status: 'off' });
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('workspaces')
        .select('plan, trial_ends_at')
        .eq('id', workspaceId)
        .maybeSingle();
      if (cancelled) return;
      if (error || !data || data.plan !== 'pro' || !data.trial_ends_at) {
        // not on a paid trial, or the row failed to load — banner absent
        setState({ status: 'off' });
        return;
      }
      const ends = new Date(data.trial_ends_at);
      if (ends.getTime() <= Date.now()) {
        setState({ status: 'off' }); // trial ended → screen 9b path
        return;
      }
      setState({
        status: 'on',
        daysLeft: daysLeft(data.trial_ends_at),
        endsAt: ends,
      });
    })();
    return () => { cancelled = true; };
  }, [workspaceId, settled]);

  if (!isSignupEnabled() || state.status !== 'on') return null;

  const urgent = state.daysLeft <= 2;
  const endDate = state.endsAt.toLocaleDateString(undefined, {
    day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <div className={`pq-trial-banner${urgent ? ' pq-trial-banner--urgent' : ''}`}>
      <span className="pq-trial-banner__icon" aria-hidden>
        <Sparkles size={18} />
      </span>
      <div className="pq-trial-banner__text">
        <strong>14-day trial · {state.daysLeft} {state.daysLeft === 1 ? 'day' : 'days'} left</strong>
        <span>
          {urgent
            ? 'After that you move to the Free plan. Your data stays safe.'
            : `You have full access to CRM until ${endDate}.`}
        </span>
      </div>
      <Link to="/plans-opening-soon" className="pq-trial-banner__cta">
        Upgrade
      </Link>
    </div>
  );
}